// src/components/WhyTrustUs.js
import React from "react";
import styled from "styled-components";
import {
  FaChartLine,
  FaUserCircle,
  FaComments,
  FaCalendarCheck,
  FaDice,
  FaUserPlus,
  FaCheck,
  FaGift,
} from "react-icons/fa";
import { motion } from "framer-motion";

/* ------------------------------------
   MAIN WRAPPER
------------------------------------ */
const Wrapper = styled.section`
  padding: 5rem 8%;
  text-align: center;
`;

/* ------------------------------------
   SECTION TITLES
------------------------------------ */
const SectionTitle = styled.h2`
  font-size: 2.8rem;
  margin-bottom: 3rem;
  color: white;
`;

/* ------------------------------------
   GRID + CARD COMPONENTS
------------------------------------ */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
`;

const GlassCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  padding: 2rem;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(12px);
  text-align: left;
  transition: 0.3s;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 10px 35px rgba(90, 110, 255, 0.3);
  }
`;

const IconWrapper = styled.div`
  font-size: 2.5rem;
  color: var(--primary-color);
  margin-bottom: 1rem;
`;

const CardTitle = styled.h3`
  color: white;
  font-size: 1.3rem;
  margin-bottom: 0.6rem;
`;

const CardDesc = styled.p`
  color: #c9cbe4;
  font-size: 1rem;
  line-height: 1.6;
`;

/* ------------------------------------
   CTA (MATCHING YOUR SCREENSHOT STYLE)
------------------------------------ */
const CTAOuter = styled.div`
  margin: 3.2rem auto 0 auto;
  max-width: 1150px;
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;

  background: radial-gradient(900px 520px at 18% 45%, rgba(120, 90, 255, 0.2), transparent 55%),
    linear-gradient(135deg, #0b1025 0%, #0e1633 55%, #0b1025 100%);
`;

const CTAGrid = styled.div`
  display: grid;
  grid-template-columns: 0.95fr 1.05fr;
  gap: 3.2rem;
  align-items: center;
  padding: 3.2rem 3rem;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    padding: 2.4rem 1.6rem;
    gap: 2.2rem;
    text-align: left;
  }
`;

const CTAVisual = styled.div`
  position: relative;
  min-height: 300px;
  display: grid;
  place-items: center;
`;

const Glow = styled.div`
  position: absolute;
  inset: -12%;
  background: radial-gradient(circle at 50% 50%, rgba(120, 90, 255, 0.22), transparent 60%);
  filter: blur(26px);
  pointer-events: none;
`;

const VisualCard = styled.div`
  width: min(360px, 92%);
  height: 340px;
  border-radius: 28px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(12px);
  position: relative;
  display: grid;
  place-items: center;
  overflow: hidden;
`;

const VisualIcon = styled.div`
  width: 160px;
  height: 160px;
  border-radius: 30px;
  background: rgba(120, 90, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.12);
  display: grid;
  place-items: center;
  color: var(--primary-color);
  font-size: 3.4rem;
`;

const Dot = styled.span`
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: rgba(120, 90, 255, 0.65);
  opacity: 0.85;
`;

const CTAContent = styled.div`
  text-align: left;
`;

const CTATitle = styled.h3`
  color: white;
  font-size: 3.1rem;
  line-height: 1.1;
  margin: 0 0 1rem 0;

  @media (max-width: 980px) {
    font-size: 2.4rem;
  }
`;

const Highlight = styled.span`
  color: var(--primary-color);
`;

const CTASub = styled.p`
  margin: 0 0 1.6rem 0;
  color: #cfd3e8;
  font-size: 1.05rem;
  line-height: 1.7;
  max-width: 580px;
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2.1rem 0;
  display: grid;
  gap: 0.9rem;
`;

const Bullet = styled.li`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  color: #e8eaff;
  font-size: 1.02rem;
  line-height: 1.6;

  svg {
    margin-top: 0.2rem;
    color: #a9b2ff;
    flex: 0 0 auto;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.95rem 1.6rem;
  border-radius: 14px;
  text-decoration: none;
  font-weight: 700;
  color: #0b0b14;
  background: var(--primary-color);
  border: 1px solid rgba(255, 255, 255, 0.14);
  min-width: 210px;

  &:hover {
    filter: brightness(1.05);
    box-shadow: 0 12px 30px rgba(120, 90, 255, 0.25);
  }
`;

const SecondaryBtn = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.95rem 1.6rem;
  border-radius: 14px;
  text-decoration: none;
  font-weight: 700;
  color: #e8eaff;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.22);
  min-width: 160px;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.32);
  }
`;

const CTADivider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
  margin: 3.2rem auto 0 auto;
  max-width: 1150px;
`;

/* ------------------------------------
   COMPARISON BARS
------------------------------------ */
const BarCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  padding: 1.8rem;
  text-align: left;
`;

const BarTitle = styled.h4`
  color: white;
  margin-bottom: 1rem;
`;

const BarTrack = styled.div`
  height: 10px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  width: ${(props) => props.percent || "50%"};
  background: ${(props) => props.color || "#4ade80"};
  border-radius: 10px;
  transition: width 1s ease;
`;

/* ------------------------------------
   FAQ SECTION
------------------------------------ */
const FAQSection = styled.div`
  margin-top: 6rem;
  text-align: left;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const FAQItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 1.3rem 1.6rem;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 1rem;
  cursor: pointer;

  &.open {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const FAQQuestion = styled.h3`
  margin: 0;
  color: white;
  font-size: 1.15rem;
`;

const FAQAnswer = styled.p`
  margin: 0;
  margin-top: 0.8rem;
  color: #cfd3e8;
  font-size: 0.95rem;
  display: none;

  ${FAQItem}.open & {
    display: block;
  }
`;

/* ------------------------------------
   MAIN COMPONENT
------------------------------------ */
const WhyTrustUs = () => {
  return (
    <Wrapper>
      {/* Trend Genie Social */}
      <SectionTitle>Trend Genie Social</SectionTitle>
      <Grid>
        {[
          {
            icon: <FaChartLine />,
            title: "Discover What’s Trending",
            desc: "Explore popular posts, new updates, and fresh content so you never miss what’s happening.",
          },
          {
            icon: <FaUserCircle />,
            title: "Profile and Community Identity",
            desc: "Build your profile, grow your presence, and be part of a focused crypto community around the clock.",
          },
          {
            icon: <FaComments />,
            title: "Social Feed & Networking",
            desc: "Stay informed with a centralized feed for updates and conversations across the Trend Genie crypto community.",
          },
        ].map((item, i) => (
          <GlassCard key={i}>
            <IconWrapper>{item.icon}</IconWrapper>
            <CardTitle>{item.title}</CardTitle>
            <CardDesc>{item.desc}</CardDesc>
          </GlassCard>
        ))}
      </Grid>

      {/* CTA #1 (Social) */}
      <CTAOuter>
        <CTAGrid>
          <CTAVisual>
            <Glow />
            <VisualCard>
              <Dot style={{ top: "22%", left: "18%" }} />
              <Dot style={{ top: "18%", right: "22%", width: 8, height: 8, opacity: 0.6 }} />
              <Dot style={{ bottom: "20%", left: "26%", width: 7, height: 7, opacity: 0.55 }} />
              <Dot style={{ bottom: "26%", right: "18%", width: 11, height: 11, opacity: 0.65 }} />
              <VisualIcon>
                <FaComments />
              </VisualIcon>
            </VisualCard>
          </CTAVisual>

          <CTAContent>
            <CTATitle>
              Your crypto community starts
              <br />
              with <Highlight>Trend Genie Social</Highlight>
            </CTATitle>

            <CTASub>
              Post updates, discover trending discussions, and connect with traders, builders, and creators inside a crypto focused social platform.
            </CTASub>

            <BulletList>
              <Bullet>
                <FaCheck />
                <span>Publish posts and engage through likes and comments in one clean feed</span>
              </Bullet>
              <Bullet>
                <FaCheck />
                <span>Build your profile and grow your presence inside the Trend Genie ecosystem</span>
              </Bullet>
              <Bullet>
                <FaCheck />
                <span>Explore what is trending without missing key community updates</span>
              </Bullet>
            </BulletList>

            <CTAButtons>
              <PrimaryBtn
                href="https://social.trendgenie.io"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                Join Trend Genie Social
              </PrimaryBtn>

              <SecondaryBtn
                href="#faq"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                Learn More
              </SecondaryBtn>
            </CTAButtons>
          </CTAContent>
        </CTAGrid>
      </CTAOuter>

      {/* Airdrop Program */}
      <SectionTitle id="airdrop" style={{ marginTop: "6rem" }}>
        Airdrop Program
      </SectionTitle>
      <Grid>
        {[
          {
            icon: <FaCalendarCheck />,
            title: "1. Daily Check Ins",
            desc: "Complete a quick daily check in to build consistency and increase your airdrop eligibility over time.",
          },
          {
            icon: <FaDice />,
            title: "2. Spin and Tasks",
            desc: "Participate through spins and simple in app tasks that contribute toward your overall airdrop progress.",
          },
          {
            icon: <FaUserPlus />,
            title: "3. Referrals",
            desc: "Invite others to join Trend Genie Social and improve your airdrop standing through verified referrals.",
          },
        ].map((item, i) => (
          <GlassCard key={i}>
            <IconWrapper>{item.icon}</IconWrapper>
            <CardTitle>{item.title}</CardTitle>
            <CardDesc>{item.desc}</CardDesc>
          </GlassCard>
        ))}
      </Grid>

      {/* Who is it for */}
      <SectionTitle style={{ marginTop: "6rem" }}>
        Who Is Trend Genie Designed For?
      </SectionTitle>
      <Grid>
        {[
          {
            title: "Crypto Traders and Investors",
            desc: "Stay updated with community insights, market talk, and trending discussions in one place.",
          },
          {
            title: "Web3 Builders and Founders",
            desc: "Share progress, get feedback, and connect with early users, collaborators, and supporters.",
          },
          {
            title: "Crypto Communities and Creators",
            desc: "Grow your presence, start conversations, and build a network around your ideas and content.",
          },
          {
            title: "Newcomers learning crypto",
            desc: "A beginner friendly space to learn the basics, follow discussions, and understand crypto through community insights and shared experiences.",
          },
        ].map((item, i) => (
          <GlassCard key={i}>
            <CardTitle>{item.title}</CardTitle>
            <CardDesc>{item.desc}</CardDesc>
          </GlassCard>
        ))}
      </Grid>

      {/* Comparison */}
      <SectionTitle style={{ marginTop: "6rem" }}>
        Trend Genie vs Traditional Trading
      </SectionTitle>
      <Grid>
        <BarCard>
          <BarTitle>Speed</BarTitle>
          <BarTrack>
            <BarFill percent="95%" color="#4ade80" />
          </BarTrack>
          <CardDesc>AI reacts instantly.</CardDesc>
        </BarCard>

        <BarCard>
          <BarTitle>Consistency</BarTitle>
          <BarTrack>
            <BarFill percent="88%" color="#60a5fa" />
          </BarTrack>
          <CardDesc>No emotional biases.</CardDesc>
        </BarCard>

        <BarCard>
          <BarTitle>Stress Level</BarTitle>
          <BarTrack>
            <BarFill percent="20%" color="#f87171" />
          </BarTrack>
          <CardDesc>Minimal stress trading.</CardDesc>
        </BarCard>
      </Grid>

      {/* CTA #2 (Airdrop) */}
      <CTADivider />
      <CTAOuter style={{ marginTop: "3.2rem" }}>
        <CTAGrid>
          <CTAVisual>
            <Glow />
            <VisualCard>
              <Dot style={{ top: "24%", left: "20%" }} />
              <Dot style={{ top: "16%", right: "24%", width: 8, height: 8, opacity: 0.6 }} />
              <Dot style={{ bottom: "22%", left: "28%", width: 7, height: 7, opacity: 0.55 }} />
              <Dot style={{ bottom: "28%", right: "20%", width: 11, height: 11, opacity: 0.65 }} />
              <VisualIcon>
                <FaGift />
              </VisualIcon>
            </VisualCard>
          </CTAVisual>

          <CTAContent>
            <CTATitle>
              Early participation gets
              <br />
              <Highlight>recognized</Highlight>
            </CTATitle>

            <CTASub>
              The airdrop program is designed to recognize early activity inside Trend Genie Social through selected in app actions and official updates.
            </CTASub>

            <BulletList>
              <Bullet>
                <FaCheck />
                <span>Build progress through check ins, spins, and eligible in app tasks</span>
              </Bullet>
              <Bullet>
                <FaCheck />
                <span>Improve standing through verified referrals and real participation</span>
              </Bullet>
              <Bullet>
                <FaCheck />
                <span>Rely on official announcements for rules, eligibility, and distribution timing</span>
              </Bullet>
            </BulletList>

            <CTAButtons>
              <PrimaryBtn
                href="https://social.trendgenie.io"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                Start Participating
              </PrimaryBtn>

              <SecondaryBtn
                href="#airdrop"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                View Details
              </SecondaryBtn>
            </CTAButtons>
          </CTAContent>
        </CTAGrid>
      </CTAOuter>

      {/* FAQ */}
      <FAQSection id="faq">
        <SectionTitle style={{ fontSize: "2.4rem", textAlign: "center" }}>
          Frequently Asked Questions
        </SectionTitle>

        {[
          {
            q: "What is Trend Genie Social?",
            a: "Trend Genie Social is a crypto focused social platform where users can post updates, engage with community content, and stay connected with what’s trending across the Trend Genie ecosystem.",
          },
          {
            q: "Is Trend Genie Social free to use?",
            a: "Yes. You can create an account and start using the platform without paying.",
          },
          {
            q: "Is Trend Genie free?",
            a: "Yes, there is a 7-day free trial with full access.",
          },
          {
            q: "What is the Trend Genie airdrop?",
            a: "The airdrop is a community program designed to recognize early participation inside Trend Genie Social through selected in app activities.",
          },
          {
            q: "How do I participate in the airdrop?",
            a: "Join Trend Genie Social and follow the in app airdrop steps. Your progress is based on eligible activities such as check ins, tasks, spins, and referrals.",
          },
          {
            q: "When will the airdrop be distributed?",
            a: "Distribution timing and eligibility rules are announced through official Trend Genie channels. Please rely on in app updates and official announcements only.",
          },
        ].map((faq, i) => (
          <FAQItem key={i} onClick={(e) => e.currentTarget.classList.toggle("open")}>
            <FAQQuestion>{faq.q}</FAQQuestion>
            <FAQAnswer>{faq.a}</FAQAnswer>
          </FAQItem>
        ))}
      </FAQSection>
    </Wrapper>
  );
};

export default WhyTrustUs;
